import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes, { array } from 'prop-types';
import classnames from 'classnames';

import { Button, H1, Icon, NoResults, Table } from '../components';
import SortOptions from '~COMPONENTS/sortOptions/SortOptions';
import SearchFilter from '~COMPONENTS/SearchFilter';
import LoadingMessage from '~COMPONENTS/spinner/LoadingMessage';
import AddressCard from '~COMPONENTS/card/AddressCard';
import PageHeader from '~COMPONENTS/containers/PageHeader';
import { TranslatedCopy } from '../intl/TranslatedCopy';
import AddressBookSidebar from './AddressBookSidebar';

import columns from './columns';

import { editAddress, sidebarOpen, mergedAddresses } from './selectors';
import { toggleSidebar, setEditAddress, requestAllAddresses, clearMergedAddresses } from './actions';
import { hasAuthCheckFinished } from '../auth/reducer';
import { addressSearchTerm } from '../global/reducer';
import { setAddressSearchTerm } from '../global/actions';
import { getUserData, getUserAccount } from '../user/selectors';
import {
  getIsGreaterChinaSalesOrg,
  getCountryProvinces,
  getIsEUSalesOrg,
  getIsJapanSalesOrg,
  getIsMANASalesOrg
} from '../gamedayAccount/selectors';

import { addressTypes } from '../types/address';
import { userAccountTypes, userTypes } from '../types/user';
import { isEqual as isEqualLodash, sortBy } from '../util/lodash';
import { getChinaProvinceFromCode } from '../util/languageTranslations';

export class AddressBookContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      addressesLoaded: false,
      showListView: false,
      allAddresses: []
    };
  }

  componentDidMount() {
    const { clearAllAddresses } = this.props;
    clearAllAddresses();
    this.refreshAddresses();
  }

  componentDidUpdate(prevProps) {
    const { allAddresses, account } = this.props;
    const prevArrStr = sortBy(prevProps.allAddresses, 'id').map(adr => JSON.stringify(adr));
    const curArrStr = sortBy(allAddresses, 'id').map(adr => JSON.stringify(adr));

    const isEqual = isEqualLodash(prevArrStr, curArrStr);

    if (prevProps.account.number !== account.number || !isEqual) {
      this.setAllAddresses(allAddresses);
      this.clearSearch();
    }
  }

  refreshAddresses = () => {
    const { requestAllAddresses } = this.props;
    requestAllAddresses();
  };

  setAddressesLoaded = bool => this.setState({ addressesLoaded: bool });

  setAllAddresses = allAddresses => {
    this.setState(prev => ({
      ...prev,
      allAddresses
    }));
  };

  setEditAddress = id => {
    const { setEditAddress } = this.props;
    const { allAddresses } = this.state;
    setEditAddress(id ? allAddresses.filter(address => address.id === id.toString())[0] : null);
  };

  searchUpdateCallback = val => {
    if (!val) this.searchClearCallback();
  };

  searchClearCallback = () => {
    this.refreshAddresses();
    this.clearSearch();
  };

  clearSearch = () => {
    const { setAddressSearchTerm } = this.props;
    return setAddressSearchTerm('');
  };

  toggleView = val => this.setState({ showListView: val === 'list' });

  handleToggleSidebar = () => {
    const { toggleSidebar } = this.props;
    this.setEditAddress(null);
    return toggleSidebar();
  };

  /**
   * @param {object} param0
   * @param {object} param0.address
   * @param {boolean} param0.isNewAddress
   * @param {*} param0.event
   */
  handleAddressClick = ({ address, isNewAddress }) => {
    const { isCheckout, setEditAddress, handleCloseModal } = this.props;
    if (!address) return null;
    setEditAddress(null);
    if (isCheckout) {
      setEditAddress(address);
      const addr = this.handleShipToAddress(address);
      if (handleCloseModal && isNewAddress) {
        handleCloseModal();
      }
      return addr;
    }

    if (address.isLocked) {
      return null;
    }

    return this.handleEdit(address);
  };

  handleEdit = address => {
    const { toggleSidebar } = this.props;
    const { id } = address;

    if (/^\w+|-|\w+$/gm.test(address)) {
      this.setEditAddress(address);
    } else if (id) {
      this.setEditAddress(id);
    }
    return toggleSidebar();
  };

  handleShipToAddress = address => {
    const { shipToAddress } = this.props;
    return shipToAddress ? shipToAddress(address) : null;
  };

  handleSortOrFilter = updatedList => {
    this.setState({ allAddresses: updatedList }, () => this.setAddressesLoaded(true));
  };

  handleAddAddressClick = () => {
    this.handleToggleSidebar();
  };

  handleProvinceName = provinceCode => {
    const { isGreaterChinaSalesOrg, isEUSalesOrg, isJapanSalesOrg, userData, provinces } = this.props;
    if (isGreaterChinaSalesOrg) {
      return getChinaProvinceFromCode(userData?.locale, provinceCode);
    }
    if (isEUSalesOrg || isJapanSalesOrg) {
      return provinces[provinceCode] || provinceCode;
    }
    return provinceCode;
  };

  renderNoResults = () => {
    const { allAddresses, addressesLoaded } = this.state;
    const { searchTerm } = this.props;
    return (
      addressesLoaded &&
      allAddresses &&
      allAddresses.length === 0 &&
      searchTerm !== '' && <NoResults titleId='addressBook.search.noresults' />
    );
  };

  render() {
    const { fetchAddresses, isCheckout, isDirectShip, sidebarOpen, editAddress, isMANASalesOrg } = this.props;
    const { allAddresses, addressesLoaded, showListView } = this.state;

    const displayAddresses = allAddresses.filter(addr => (isDirectShip ? addr.isLocked : true));

    return (
      <>
        <div className={classnames('addressbook__main_outerWrapper', { hidden: sidebarOpen })}>
          {isCheckout ? (
            <H1 className='mt12-sm mb5-sm ta-sm-c'>
              <TranslatedCopy id='addressBook.header.select.address' />
            </H1>
          ) : (
            <PageHeader page='addressBook' title='addressBook.route' className='flex-container row bg-white'>
              <div className='d-sm-h d-lg-b'>
                <Button
                  type='button'
                  extraClassName='ncss-btn-primary-dark'
                  onClick={this.handleAddAddressClick}
                  data-desc='addressBook__header_new_address'
                >
                  <TranslatedCopy id='addressBook.addButton' />
                </Button>
              </div>
            </PageHeader>
          )}
          <div className='addressbook__main_wrapper bg-primary-grey pt2-sm pb4-sm prl3-sm prl6-md'>
            <div className='flex-container row space-between align-center u-full-width'>
              <SearchFilter
                label={new TranslatedCopy({ id: 'addressBook.search.label' }).toString()}
                list={displayAddresses}
                filterBy={['name', 'attention', 'addressLine1', 'city', 'state', 'zipCode', 'country']}
                pageName='addressBook'
                setList={this.handleSortOrFilter}
                clearList={this.searchClearCallback}
                updateList={this.searchUpdateCallback}
                blnAddressBook
              />
              <SortOptions
                list={displayAddresses}
                sortBy={[
                  { value: 'name', name: 'Name' },
                  { value: 'attention', name: 'Attention' },
                  { value: 'addressLine1', name: 'Address' },
                  { value: 'city', name: 'City' },
                  { value: 'state', name: 'State' }
                ]}
                defaultValue={{ value: 'name', name: 'Name' }}
                toggleView={!isCheckout ? this.toggleView : null}
                handleSortOptionsChange={this.handleSortOrFilter}
                currentView={showListView ? 'list' : 'grid'}
              />
            </div>
            {!addressesLoaded && (
              <LoadingMessage title={new TranslatedCopy({ id: 'route.address.title' }).toString()} />
            )}
            {this.renderNoResults()}
            <div className={`${showListView ? 'hide-view' : ''}`}>
              <div className='card-view flex-container mauto-sm ta-sm-l'>
                {displayAddresses &&
                  displayAddresses.map(address =>
                    address.isLocked === false ? (
                      <AddressCard
                        {...address}
                        state={this.handleProvinceName(address.state)}
                        careOf={isMANASalesOrg ? address.careOf : undefined}
                        id={address.id}
                        key={`addressCard-${address.id}`}
                        onClick={event => this.handleAddressClick({ address, event })}
                        isSelected={editAddress ? address.id === editAddress.id : false}
                        handleEdit={this.handleEdit}
                        isCheckout={isCheckout}
                      />
                    ) : (
                      <AddressCard
                        {...address}
                        state={this.handleProvinceName(address.state)}
                        careOf={isMANASalesOrg ? address.careOf : undefined}
                        key={`sapAddressCard-${address.id || address.account}`}
                        onClick={event => this.handleAddressClick({ address, event })}
                        isSelected={editAddress ? address.account === editAddress.account : false}
                        isCheckout={isCheckout}
                      />
                    )
                  )}
                {addressesLoaded && !isDirectShip && (
                  <div
                    className={classnames(
                      'card add-address-card',
                      'bg-white flex-container ta-sm-c ml3-lg mr2-lg mb4-sm pt5-sm pb5-sm prl4-sm'
                    )}
                    onClick={this.handleAddAddressClick}
                    data-desc='address__card_add-address'
                  >
                    <div className='add-address-card__button prl1-sm'>
                      <Icon name='add' alt='add address' />
                    </div>
                    <H1>
                      <TranslatedCopy id='addressBook.addButtonTile' />
                    </H1>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={`table-view ${showListView ? '' : 'hide-view'}`}>
            <Table
              data={displayAddresses}
              columns={columns}
              handleClick={this.handleAddressClick}
              selectedId={editAddress && editAddress.id}
            />
          </div>
        </div>
        {sidebarOpen ? (
          <AddressBookSidebar
            isCheckout={isCheckout}
            fetchAddresses={fetchAddresses}
            toggleSidebar={this.handleToggleSidebar}
            handleAddressClick={this.handleAddressClick}
          />
        ) : null}
      </>
    );
  }
}

AddressBookContent.propTypes = {
  account: PropTypes.any,
  addresses: PropTypes.arrayOf(PropTypes.shape(addressTypes)),
  sapAddresses: PropTypes.arrayOf(PropTypes.shape(addressTypes)),
  editAddress: PropTypes.object,
  fetchAddresses: PropTypes.func.isRequired,
  hasAuthCheckFinished: PropTypes.bool,
  isCheckout: PropTypes.bool,
  isDirectShip: PropTypes.bool,
  searchTerm: PropTypes.string,
  setAddressSearchTerm: PropTypes.func.isRequired,
  setEditAddress: PropTypes.func.isRequired,
  shipToAddress: PropTypes.func,
  sidebarOpen: PropTypes.bool,
  toggleSidebar: PropTypes.func.isRequired,
  userAccount: userAccountTypes,
  userData: userTypes,
  handleCloseModal: PropTypes.func,
  requestAllAddresses: PropTypes.func.isRequired,
  clearAllAddresses: PropTypes.func.isRequired,
  allAddresses: array,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  isEUSalesOrg: PropTypes.bool.isRequired,
  isJapanSalesOrg: PropTypes.bool.isRequired,
  isMANASalesOrg: PropTypes.bool.isRequired,
  provinces: PropTypes.object,
  shipToId: PropTypes.string
};

AddressBookContent.defaultProps = {
  account: {},
  addresses: null,
  sapAddresses: null,
  editAddress: null,
  hasAuthCheckFinished: false,
  isCheckout: false,
  isDirectShip: false,
  handleCloseModal: null,
  searchTerm: '',
  shipToAddress: null,
  sidebarOpen: false,
  userAccount: null,
  userData: null,
  allAddresses: [],
  provinces: null,
  shipToId: null
};

const mapStateToProps = (state, { shipToId }) => ({
  account: getUserAccount(state),
  hasAuthCheckFinished: hasAuthCheckFinished(state),
  editAddress: editAddress(state),
  searchTerm: addressSearchTerm(state),
  sidebarOpen: sidebarOpen(state),
  userData: getUserData(state),
  allAddresses: shipToId
    ? mergedAddresses(state)?.filter(address => address.type === 'GAMEDAY' || address.shipToId === shipToId)
    : mergedAddresses(state),
  isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state),
  isEUSalesOrg: getIsEUSalesOrg(state),
  isJapanSalesOrg: getIsJapanSalesOrg(state),
  isMANASalesOrg: getIsMANASalesOrg(state),
  provinces: getCountryProvinces(state)
});

const connector = connect(mapStateToProps, {
  requestAllAddresses,
  clearAllAddresses: clearMergedAddresses,
  setEditAddress,
  toggleSidebar,
  setAddressSearchTerm
});

export default connector(AddressBookContent);
